import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Dialog } from "../../../design-v2";
import useAuthBootstrap from "../../../auth/useAuthBootstrap";
import FirstUsePreferenceSheet, {
  firstUseMotionDuration,
} from "../components/FirstUsePreferenceSheet";
import { startWorkoutExecution } from "../api/workoutLogApi";
import { createLocalExecutionRecord } from "../domain/executionReducer";
import { createClientUuid } from "../domain/clientIds";
import { needsOlympicConvention, needsWeightUnit } from "../domain/executionUi";
import { workoutLogStore } from "../store/localExecutionStore";
import {
  getOrCreateDeviceId,
  requestPersistentWorkoutStorage,
} from "../store/deviceIdentity";
import useConnectivity from "../hooks/useConnectivity";
import useWorkoutPreferences from "../hooks/useWorkoutPreferences";

export function sourceFromSearch(search) {
  const params = new URLSearchParams(search);
  const kind = String(params.get("kind") || "FREE").toUpperCase();
  if (kind === "CYCLE_OCCURRENCE") {
    return { kind, cycleId: params.get("cycleId"), occurrenceKey: params.get("occurrenceKey") };
  }
  if (kind === "CYCLE_WORKOUT") {
    return { kind, cycleId: params.get("cycleId"), workoutId: params.get("workoutId") };
  }
  if (kind === "WEEKLY_PLAN_WORKOUT") {
    return {
      kind,
      weeklyPlanParentId: params.get("weeklyPlanParentId"),
      workoutId: params.get("workoutId"),
    };
  }
  if (kind === "TRAIN_AGAIN") {
    return {
      kind,
      executionId: params.get("executionId") || params.get("sourceExecutionId"),
    };
  }
  return { kind: "FREE" };
}

function currentTimezone() {
  try {
    const value = Intl.DateTimeFormat().resolvedOptions().timeZone;
    new Intl.DateTimeFormat("en", { timeZone: value }).format();
    return value;
  } catch (_error) {
    return "UTC";
  }
}

function requiredStartPreferences(execution, preferences = {}) {
  const profiles = (execution?.snapshot?.blocks || []).flatMap((block) =>
    (block.exercises || []).map((exercise) => exercise.profile).filter(Boolean)
  );
  const steps = [];
  if (!preferences.weightUnit && profiles.some(needsWeightUnit)) {
    steps.push("weightUnit");
  }
  if (
    !preferences.olympicBarbellConvention &&
    profiles.some(needsOlympicConvention)
  ) {
    steps.push("olympicBarbellConvention");
  }
  return steps;
}

export default function StartWorkoutScreen() {
  const { user } = useAuthBootstrap();
  const location = useLocation();
  const navigate = useNavigate();
  const { online } = useConnectivity();
  const preferenceState = useWorkoutPreferences(user?.id, { online });
  const executionIdRef = useRef(null);
  const startedAtRef = useRef(null);
  const controllerRef = useRef(null);
  const progressTimerRef = useRef(null);
  const preferenceExitTimerRef = useRef(null);
  const preferencePendingRef = useRef(false);
  const automaticStartRef = useRef(false);
  const [starting, setStarting] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [error, setError] = useState("");
  const [extraConfirmed, setExtraConfirmed] = useState(false);
  const [pendingStart, setPendingStart] = useState(null);
  const [preferenceFlow, setPreferenceFlow] = useState(null);
  const [preferencePending, setPreferencePending] = useState(false);
  const [preferenceExiting, setPreferenceExiting] = useState(false);

  const source = sourceFromSearch(location.search);
  const projectedOccurrence = location.state?.occurrence || null;
  const needsExtraConfirmation = source.kind === "CYCLE_OCCURRENCE" &&
    projectedOccurrence?.projectionIfStartedNow?.outcome === "EXTRA";

  useEffect(() => () => {
    controllerRef.current?.abort();
    clearTimeout(progressTimerRef.current);
    clearTimeout(preferenceExitTimerRef.current);
  }, []);

  async function openExecution(response, deviceId) {
    const record = createLocalExecutionRecord({
      userId: user.id,
      deviceId,
      execution: response.execution,
      // Additive since Phase 9: absent on an older server or a history failure.
      history: response.history,
      // Exercise notes carried from saved history; absent on an older server.
      exerciseNotes: response.exerciseNotes,
      clientStartedAt: startedAtRef.current,
    });
    await workoutLogStore.putExecution(record);
    navigate(`/train/session/${record.executionId}`, { replace: true });
  }

  async function start({ confirmedExtra = false } = {}) {
    if (needsExtraConfirmation && !confirmedExtra && !extraConfirmed) return;
    if (confirmedExtra) setExtraConfirmed(true);
    if (!online) {
      setError("Starting a workout needs a connection");
      return;
    }
    setStarting(true);
    setError("");
    setShowProgress(false);
    if (!executionIdRef.current) executionIdRef.current = createClientUuid();
    if (!startedAtRef.current) startedAtRef.current = new Date().toISOString();
    progressTimerRef.current = setTimeout(() => setShowProgress(true), 400);
    const controller = new AbortController();
    controllerRef.current = controller;
    const timeout = setTimeout(() => controller.abort(), 60000);
    try {
      void requestPersistentWorkoutStorage().catch(() => {});
      const deviceId = await getOrCreateDeviceId();
      const response = await startWorkoutExecution({
        executionId: executionIdRef.current,
        deviceId,
        clientStartedAt: startedAtRef.current,
        deviceTimezone: currentTimezone(),
        clientSchemaVersion: 1,
        source: sourceFromSearch(location.search),
      }, { signal: controller.signal });
      if (response.preferences) {
        await workoutLogStore.cachePreferences(user.id, response.preferences);
      }
      const preferences = response.preferences || preferenceState.preferences || {};
      const requiredSteps = requiredStartPreferences(response.execution, preferences);
      if (requiredSteps.length) {
        setPendingStart({ response, deviceId, preferences });
        setPreferenceFlow({ steps: requiredSteps, index: 0 });
      } else {
        await openExecution(response, deviceId);
      }
    } catch (caught) {
      if (caught?.code === "OPEN_EXECUTION_EXISTS") {
        navigate("/train", { replace: true, state: { recoverOpenExecution: true } });
        return;
      }
      setError(caught?.name === "AbortError"
        ? "Starting took too long. Retry uses the same workout, so it is safe."
        : caught?.message || "Workout couldn't be started.");
    } finally {
      clearTimeout(timeout);
      clearTimeout(progressTimerRef.current);
      controllerRef.current = null;
      setStarting(false);
      setShowProgress(false);
    }
  }

  async function answerPreference(value) {
    const preferenceKind = preferenceFlow?.steps[preferenceFlow.index] || null;
    if (!pendingStart || !preferenceKind || preferencePendingRef.current) return;
    preferencePendingRef.current = true;
    setPreferencePending(true);
    setError("");
    try {
      const result = await preferenceState.answer({ [preferenceKind]: value });
      const preferences = {
        ...pendingStart.preferences,
        ...result.preferences,
        [preferenceKind]: value,
      };
      const nextIndex = preferenceFlow.index + 1;
      if (nextIndex < preferenceFlow.steps.length) {
        setPendingStart({ ...pendingStart, preferences });
        setPreferenceFlow({ ...preferenceFlow, index: nextIndex });
      } else {
        setPreferenceExiting(true);
        await new Promise((resolve) => {
          preferenceExitTimerRef.current = setTimeout(
            resolve,
            firstUseMotionDuration(280)
          );
        });
        await openExecution(pendingStart.response, pendingStart.deviceId);
      }
    } catch (caught) {
      setPreferenceExiting(false);
      setError(caught?.message || "That preference couldn't be saved.");
    } finally {
      preferencePendingRef.current = false;
      setPreferencePending(false);
    }
  }

  useEffect(() => {
    if (
      user?.id &&
      !preferenceState.loading &&
      !needsExtraConfirmation &&
      !automaticStartRef.current
    ) {
      automaticStartRef.current = true;
      void start();
    }
    // Start is intentionally one-shot for this mounted route. Retry remains explicit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsExtraConfirmation, preferenceState.loading, user?.id]);

  const showExtraConfirmation = needsExtraConfirmation && !extraConfirmed;
  const preferenceKind = preferenceFlow?.steps[preferenceFlow.index] || null;
  return (
    <section className="lz-workout-centered lz-workout-start-transition" aria-busy={starting || undefined}>
      <div className="lz-workout-start-transition__status" aria-live="polite">
        <span className="lz-workout-start-transition__spinner" aria-hidden="true" />
        <p>{showProgress ? "Preparing your workout…" : "Opening your workout…"}</p>
        {error ? <p role="alert" className="lz-workout-error">{error}</p> : null}
        {error && !showExtraConfirmation ? (
          <Button disabled={starting} onClick={() => start({ confirmedExtra: extraConfirmed })}>
            Retry
          </Button>
        ) : null}
      </div>
      <Dialog
        open={showExtraConfirmation}
        dismissible={false}
        showCloseButton={false}
        title="This will be an Extra Workout"
        description={projectedOccurrence?.weekNumber
          ? `This workout is planned for Week ${projectedOccurrence.weekNumber}. Doing it now logs it as an Extra Workout.`
          : "Doing this workout now logs it as an Extra Workout."}
      >
        <div className="lz-workout-dialog-actions">
          {error ? <p role="alert" className="lz-workout-error">{error}</p> : null}
          <Button
            disabled={starting}
            isLoading={starting && showProgress}
            loadingLabel="Preparing workout…"
            onClick={() => start({ confirmedExtra: true })}
          >
            Start Workout
          </Button>
          <Button variant="ghost" disabled={starting} onClick={() => navigate("/train", { replace: true })}>Cancel</Button>
        </div>
      </Dialog>
      <FirstUsePreferenceSheet
        open={Boolean(preferenceKind)}
        kind={preferenceKind}
        pending={preferencePending}
        stepCount={preferenceFlow?.steps.length || 1}
        stepIndex={preferenceFlow?.index || 0}
        exiting={preferenceExiting}
        onChoose={answerPreference}
      />
    </section>
  );
}

export { requiredStartPreferences };
